// Career Map — build/SSR-time data derivation (Stage 1).
//
// Pure functions over the static career JSON + the sphere mapping. No
// network, no Supabase, no DB. Everything the hub and sphere pages render
// (counts, chip lists, hub search index, card examples) is computed here
// from careers_1822_{az,ru,en}.json so it can never drift from the data
// the scoring engine uses. The verify script (prebuild) guards the mapping
// invariant; this module trusts it and just projects the data.

import azData from "../data/careers_1822_az.json";
import ruData from "../data/careers_1822_ru.json";
import enData from "../data/careers_1822_en.json";
import mapping from "../data/career_map_spheres.json";
import { SPHERES, UI, type Lang, type SphereMeta } from "./spheres";

type CareerRecord = { id: string; name: string };
type ClusterRecord = { id: string; name: string; careers: CareerRecord[] };
type CareersFile = { clusters: ClusterRecord[] };

const FILES: Record<Lang, CareersFile> = {
  az: azData as unknown as CareersFile,
  ru: ruData as unknown as CareersFile,
  en: enData as unknown as CareersFile,
};

const LOCALES: Lang[] = ["az", "ru", "en"];

// Search normalization: case-insensitive + diacritic-insensitive, so a query
// typed in plain ASCII (or any locale's spelling) matches every locale's name.
// Base letters O*NET/AZ special-case (schwa, dotless/dotted i) are mapped
// explicitly; everything else (ş ç ğ ö ü ё …) decomposes under NFD and we drop
// the combining marks. Applied identically to the index and the query.
export function normalizeSearch(s: string): string {
  return s
    .replace(/[əƏ]/g, "e")
    .replace(/[ıİ]/g, "i")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim();
}

// career id → its name in every locale, built once. Lets the (single) search
// index carry all three spellings while the row still displays the active one.
const NAMES_BY_ID: Record<string, Record<Lang, string>> = {};
for (const lang of LOCALES) {
  for (const cluster of FILES[lang].clusters) {
    for (const career of cluster.careers) {
      (NAMES_BY_ID[career.id] ||= { az: "", ru: "", en: "" })[lang] = career.name;
    }
  }
}

function clustersOf(slug: string): string[] {
  const entry = (mapping as { slug: string; clusters: string[] }[]).find((m) => m.slug === slug);
  return entry ? entry.clusters : [];
}

/** All careers of a sphere ({id, name}), in cluster order then career order. */
export function careersOfSphere(slug: string, lang: Lang): CareerRecord[] {
  return careerEntries(slug, lang);
}

function careerEntries(slug: string, lang: Lang): CareerRecord[] {
  const wanted = new Set(clustersOf(slug));
  const out: CareerRecord[] = [];
  for (const cluster of FILES[lang].clusters) {
    if (wanted.has(cluster.id)) {
      for (const career of cluster.careers) out.push({ id: career.id, name: career.name });
    }
  }
  return out;
}

// ── Per-career lookup maps (built once) ─────────────────────────────────
// The detail pages need a career's cluster (→ sphere), its entry tag and its
// riasec code without re-scanning the tree each render.
const CLUSTER_OF_ID: Record<string, string> = {};
const ENTRY_OF_ID: Record<string, string> = {};
const RIASEC_OF_ID: Record<string, string> = {};
for (const cluster of FILES.az.clusters) {
  for (const career of cluster.careers) {
    const c = career as { id: string; entry?: string; riasec?: string };
    CLUSTER_OF_ID[c.id] = cluster.id;
    ENTRY_OF_ID[c.id] = c.entry ?? "";
    RIASEC_OF_ID[c.id] = c.riasec ?? "";
  }
}
const SPHERE_SLUG_OF_CLUSTER: Record<string, string> = {};
for (const { slug, clusters } of mapping as { slug: string; clusters: string[] }[])
  for (const c of clusters) SPHERE_SLUG_OF_CLUSTER[c] = slug;

/** cluster id a career belongs to (undefined if the id is unknown). */
export function clusterOf(id: string): string | undefined {
  return CLUSTER_OF_ID[id];
}
/** sphere slug a career belongs to, via its cluster. */
export function sphereSlugOf(id: string): string | undefined {
  const cl = CLUSTER_OF_ID[id];
  return cl ? SPHERE_SLUG_OF_CLUSTER[cl] : undefined;
}
/** localized career name (undefined if unknown). */
export function careerNameOf(id: string, lang: Lang): string | undefined {
  return NAMES_BY_ID[id]?.[lang];
}
/** entry tag (bachelor/master/…) — "" if unknown. */
export function entryOf(id: string): string {
  return ENTRY_OF_ID[id] ?? "";
}
/** record riasec code (live scoring value) — "" if unknown. */
export function riasecOf(id: string): string {
  return RIASEC_OF_ID[id] ?? "";
}
/** sphere display metadata for a slug. */
export function sphereMetaOf(slug: string): SphereMeta | undefined {
  return SPHERES.find((s) => s.slug === slug);
}

export const TOTAL_SPHERES = SPHERES.length;

export const TOTAL_CAREERS = FILES.az.clusters.reduce((n, c) => n + c.careers.length, 0);

export type HubSphere = {
  slug: string;
  icon: string;
  name: string;
  subtitle: string;
  count: number;
  examples: string[]; // up to 3, straight from the data (localized)
  /**
   * Профессии сферы, у которых ЕСТЬ своя страница — для указателя на каталоге.
   *
   * Отдельно от count: count говорит, сколько профессий в сфере вообще (290 на
   * весь справочник), а сюда попадают только те, куда есть куда вести. Ссылка
   * на несуществующую страницу — обещание, которого каталог не сдержит.
   */
  carded: { id: string; name: string }[];
};

export type SearchEntry = {
  /** career id — anchors the sphere-page deep link (#id). */
  id: string;
  /** display name in the active locale. */
  name: string;
  /** sphere slug (link target). */
  slug: string;
  /** sphere display name in the active locale. */
  sphere: string;
  /** normalized az+ru+en names, so one query matches any locale's spelling. */
  hay: string;
};

export type HubData = {
  lang: Lang;
  totalCareers: number;
  totalSpheres: number;
  spheres: HubSphere[];
  search: SearchEntry[];
};

/**
 * Множество заведённых страниц приходит ПАРАМЕТРОМ, а не импортом.
 *
 * data.ts — нижний слой: careerPages.ts читает его, а не наоборот. Импорт в
 * обратную сторону замкнул бы круг, и модуль, собирающий страницы, стал бы
 * нужен каталогу просто чтобы тот пересчитал свои карточки.
 */
export function buildHub(lang: Lang, cardedIds: ReadonlySet<string> = new Set()): HubData {
  const spheres: HubSphere[] = SPHERES.map((meta: SphereMeta) => {
    const careers = careerEntries(meta.slug, lang);
    return {
      slug: meta.slug,
      icon: meta.icon,
      name: meta.name[lang],
      subtitle: meta.subtitle[lang],
      count: careers.length,
      examples: careers.slice(0, 3).map((c) => c.name),
      carded: careers.filter((c) => cardedIds.has(c.id)).map((c) => ({ id: c.id, name: c.name })),
    };
  });

  // Client-side search index: every career → its sphere. The display name is
  // the active locale's; the match haystack fuses all three locales (normalized)
  // so an EN visitor typing an AZ/RU spelling — or vice-versa — still finds it.
  const search: SearchEntry[] = [];
  for (const meta of SPHERES) {
    for (const career of careerEntries(meta.slug, lang)) {
      const all = NAMES_BY_ID[career.id] ?? { az: career.name, ru: career.name, en: career.name };
      search.push({
        id: career.id,
        name: career.name,
        slug: meta.slug,
        sphere: meta.name[lang],
        hay: normalizeSearch(`${all.az} ${all.ru} ${all.en}`),
      });
    }
  }

  return {
    lang,
    totalCareers: TOTAL_CAREERS,
    totalSpheres: TOTAL_SPHERES,
    spheres,
    search,
  };
}

export type SphereData = {
  lang: Lang;
  slug: string;
  name: string;
  subtitle: string;
  count: number;
  careers: { id: string; name: string }[]; // id anchors the deep link from search
};

/** Returns null for an unknown slug (route should 404). */
export function buildSphere(slug: string, lang: Lang): SphereData | null {
  const meta = SPHERES.find((s) => s.slug === slug);
  if (!meta) return null;
  const careers = careerEntries(slug, lang);
  return {
    lang,
    slug,
    name: meta.name[lang],
    subtitle: meta.subtitle[lang],
    count: careers.length,
    careers,
  };
}

export { UI, type Lang };
